import {List, TextField, Typography, useMediaQuery} from '@mui/material'
import Grid from '@mui/material/Grid'
import {Box} from '@mui/system'
import {useEffect, useMemo, useState} from 'react'
import {track, useContent} from '../../logic'
import {usePageTitle} from '../../logic/analytics/usePageTitle'
import {NothingHere, DoubleCloudBox, LoadingScreen, ArchivedClosingSummary} from '../shared'
import {ActiveClosingSummary} from '../shared/ActiveClosingSummary'
import {ErrorDialog} from '../shared/dialogs/ErrorDialog'
import {partition, debounce} from 'lodash'
import {useSearchParams} from 'react-router-dom'
import {Legalese} from '../shared/Legalese'
import {useAppTheme} from '../../logic/useAppTheme'
import {useGetOrdersNew} from '../../logic/useApiQuery'
import {RefreshButton} from '../shared/RefreshButton'
import Pagination from '@mui/material/Pagination'

export function ActiveClosingsPageNew({drawerHeight}: {
  drawerHeight: number
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    isFetching,
    error,
    data: {data: orders, metadata} = {
      data: [],
      metadata: {
        page: 1,
        pageSize: 15,
        total: 0
      }
    },
    refetch
  } = useGetOrdersNew(searchParams, {
    keepPreviousData: true
  })
  const [inputValue, setInputValue] = useState(searchParams.get('q') || '')

  const debouncedSetSearchParams = useMemo(() => debounce((newValue: string) => {
    const newSearchParams = new URLSearchParams(searchParams)
    if (newValue) {
      newSearchParams.set('q', newValue)
    } else {
      newSearchParams.delete('q')
    }
    newSearchParams.set('page', '1')
    setSearchParams(newSearchParams, {replace: true})
  }, DEBOUNCE_TIME), [searchParams, setSearchParams])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    debouncedSetSearchParams(newValue)
  }

  const theme = useAppTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const {ClosingsText} = useContent()

  useEffect(() => {
    // Clear role specific search param.
    if (searchParams.has('role')) {
      searchParams.delete('role')
      setSearchParams(searchParams, {
        replace: true
      })
    }
  }, [searchParams, setSearchParams])

  usePageTitle(ClosingsText.toUpperCase())

  if (error) {
    track('error', 'useGetOrders', {}, {
      message: JSON.stringify(error),
      component: 'ActiveClosingsPage'
    })

    // @ts-ignore
    if (!error?.userErrorMessage && !error?.message) {
      // @ts-ignore
      error.userErrorMessage = `We were unable to load your ${ClosingsText}`
    }

    return <ErrorDialog error={error}/>
  }

  let [activeOrders, archivedOrders] = partition(orders, ['IsArchived', false])

  const totalPages = Math.ceil(metadata.total / metadata.pageSize)

  const handlePageChange = (_e: any, page: number) => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set('page', page.toString())
    setSearchParams(newSearchParams, {replace: true})
  }

  return (
    <>
      {isFetching && (
        <Box sx={{position: 'fixed', top: isSmall ? drawerHeight : 0, left: 0, width: '100%', zIndex: 2}}>
          <LoadingScreen/>
        </Box>
      )}
      <Box
        sx={{height: '100%', display: 'flex', flexDirection: 'column'}}
        p={{xs: '30px 12px 12px 12px', sm: '25px 20px 20px 20px'}}
      >
        <Box sx={{
          display: 'flex', p: '0 20px',
          justifyContent: 'space-between',
          mt: {xs: '8px', sm: '112px'}
        }}>
          <Box sx={{
            fontSize: 36,
            fontWeight: 300,
            width: {xs: 240, sm: '100%'},
            marginBottom: '32px',
            marginRight: '20px',
            lineHeight: '36px'
          }}>
            Your active {ClosingsText}
          </Box>
          <Box sx={{mt: {xs: '-8', sm: '-37px'}}}>
            <DoubleCloudBox></DoubleCloudBox>
          </Box>
        </Box>
        <Box display="flex" flexDirection={'row'} gap={2} mb={2}>
          <TextField
            size="small"
            value={inputValue}
            onChange={handleSearchChange}
            label={`Filter ${ClosingsText}`}
            sx={{
              width: 300, borderRadius: '20px',
              '.MuiInputBase-root': {borderRadius: '10px'}
            }}
          />
          <RefreshButton refetch={refetch} isFetching={isFetching}/>
        </Box>

        {(activeOrders?.length || isFetching) ?
          (<Grid container spacing={2}>
            {activeOrders.map(closing => (
              <Grid item xs={12} md={6} xl={4} key={closing.UserOrderKey}>
                <ActiveClosingSummary order={closing}/>
              </Grid>
            ))}
          </Grid>) :
          <NothingHere><>We couldn't find any
            active{inputValue ? ` ${ClosingsText} that match your search` : ` ${ClosingsText}`}</>
          </NothingHere>
        }

        {archivedOrders?.length ?
          <>
            <Typography sx={{
              mt: 5,
              mb: 1,
              ml: 1,
              fontSize: '18px',
              fontWeight: 500,
              lineHeight: '22px',
              letterSpacing: '0.10000000149011612px',
              textAlign: 'left'
            }}>Archived {ClosingsText}</Typography>

            <List>
              {archivedOrders.map((closing, idx) => (
                <ArchivedClosingSummary index={idx} order={closing} key={closing.UserOrderKey}></ArchivedClosingSummary>
              ))}
            </List>

          </> : ''
        }

        <Box display="flex" justifyContent="center" mt={4} mb={4} sx={{width: '100%'}}>
          <Pagination
            count={totalPages}
            size="large"
            page={metadata.page}
            onChange={handlePageChange}
            siblingCount={isSmall ? 0 : 2}
            boundaryCount={isSmall ? 0 : 1}
            color="primary"
            sx={{width: 'fit-content'}}
          />
        </Box>

        {isSmall ?
          <Box sx={{mt: 'auto', pt: 2}}>
            <Legalese></Legalese>
          </Box> : ''
        }
      </Box>
    </>
  )
}

const DEBOUNCE_TIME = 250